<template>
  <div
    :class="{
      subtab: isSubtab,
      active,
      'vertical-tab': stackVertically,
      'horizontal-tab': !stackVertically,
    }"
    data-cy="side-tab"
    :data-test="`subtab-${details.secondaryAppPath}-${details.path}`"
    class="tab-container"
    :style="containerStyle"
    @click="handleClick"
  >
    <div
      v-if="isSubtab && !originalTabColours"
      class="position-absolute sub-tab-border"
      :class="{ active }"
      :style="subTabStyle"
    />
    <div ref="tabInner" :style="innerStyle" class="d-flex h-100 align-items-center tab-inner">
      <TabImage
        v-if="details.showIcon"
        :story-path="details.path.toString()"
        :size="minDimension"
        :fixed-width="!stackVertically"
        :alt="title"
        :secondary-app-icon-id="secondaryAppIconId"
      />
      <div
        v-if="details.showLabel"
        ref="tabLabel"
        class="tab-label px-2"
        :title="showLabelAltHover ? title : undefined"
        :class="{
          tabLabelStyleA: !details.showIcon,
          'truncate-multiline-text': stackVertically,
          'text-truncate': !stackVertically,
          'd-inline-block': !stackVertically,
          'flex-grow-1': !stackVertically,
        }"
      >
        {{ title }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Component from 'vue-class-component'
import { CSSProperties } from 'vue/types/jsx'
import Vue, { PropType } from 'vue'
import TabImage from '@/components/public/SideTabs/TabImage.vue'
import { Tab } from '@/types'
import { VIEW_PUBLIC } from '@/constants'
import { makeDarker } from '@/helpers'
const components = { TabImage }

const props = {
  details: {
    type: Object as PropType<Tab>,
    required: true,
  },
}

@Component({ components })
export default class SideTab extends Vue.extend({ props }) {
  showLabelAltHover: boolean = false
  tabOffset = '15px'
  stackThresholdHeight = 70
  width = 0
  resizeObserver: ResizeObserver | null = null

  get minDimension() {
    return Math.min(this.height, this.width)
  }

  get innerStyle() {
    const x = this.stackVertically ? 0.15 : 0.3
    return {
      'font-size': this.minDimension * x + 'px',
    } as CSSProperties
  }

  get subTabStyle() {
    return {
      'border-color': this.details.barColour,
      height: this.height + 'px',
    } as CSSProperties
  }

  get containerStyle() {
    const background =
      this.originalTabColours && this.isSecondaryAppTab
        ? this.details.barColour
        : this.details.colour
    return {
      height: this.height + 'px',
      color: this.details.tabFontColor,
      'border-top': `1px solid ${makeDarker(background ?? '#000')}`,
      'background-color': background,
    } as CSSProperties
  }

  get display() {
    return this.$typedStore.public.display
  }

  async handleClick() {
    if (this.details.secondaryAppPath) {
      if (this.details.secondaryAppPath !== this.display.activeTab?.secondaryAppPath) {
        this.display.setSwitchingSecondaryApps(true)
      }

      await this.display.updateSecondaryStoryPath({
        primaryStoryPath: this.details.secondaryAppPath,
        secondaryStoryPath: this.details.path,
        mainpanel: VIEW_PUBLIC,
      })
    } else {
      this.display.updatePrimaryStoryPath(this.details.path)
    }

    this.$analytics.event('click', {
      event_category: 'side-tab',
      event_label: this.details.path,
    })
  }

  async mounted() {
    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this.$refs.tabLabel as HTMLElement)
    this.handleResize()
  }

  beforeDestroy() {
    this.resizeObserver && this.resizeObserver.disconnect()
  }

  handleResize() {
    this.$nextTick(() => {
      const ref = this.$refs.tabInner as Element
      if (ref) {
        this.width = ref.clientWidth
      }
      const tabLabel = this.$refs.tabLabel as HTMLElement | null
      if (tabLabel instanceof HTMLElement) {
        this.showLabelAltHover =
          tabLabel.scrollWidth > tabLabel.offsetWidth ||
          tabLabel.scrollHeight > tabLabel.offsetHeight
      }
    })
  }

  async updated() {
    this.handleResize()
  }

  get originalTabColours() {
    return this.$typedStore.primary.app.config.appConfig?.originalTabColours ?? false
  }

  get secondaryAppIconId() {
    return this.details.secondaryAppPath ? this.details.iconId : null
  }

  get isSecondaryAppTab() {
    return !!this.details.secondaryAppPath
  }

  get isSubtab() {
    return this.isSecondaryAppTab && !this.details.isSecondaryInitialTab
  }

  get stackVertically() {
    return this.height > this.stackThresholdHeight
  }

  get height() {
    return this.$typedStore.activeVisualisation.visibleTabHeight
  }

  get active() {
    return this.display.activeTab === this.details
  }

  get title() {
    return this.details.label || this.details.name
  }
}
</script>

<style lang="scss" scoped>
@import '@/components/public/SideTabs/tabs.scss';
</style>
